import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Section, SegmentedControl } from "@hicya/ui";

import { getSettingsSection, SETTINGS_SECTIONS } from "@/components/settings/settings-navigation";
import appearance from "@/components/settings/appearance-choices.module.scss";
import stats from "@/routes/stats.module.scss";
import settings from "@/routes/settings.module.scss";
import detail from "@/routes/habit-stats.module.scss";
import form from "@/routes/habit-edit.module.scss";
import type { AnalyticsRange } from "@/hooks/use-analytics-data";

import styles from "./route-loading.module.scss";

import type { ReactNode } from "react";

export function LoadingRegion({
  children,
  label = "Loading habits",
}: {
  children: ReactNode;
  label?: string;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 400);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div className={styles.region} aria-busy="true" data-loading-region>
      {visible && (
        <span className={styles.srOnly} role="status">
          {label}
        </span>
      )}
      <div aria-hidden="true" className={visible ? undefined : styles.concealed}>
        {children}
      </div>
    </div>
  );
}

export function Placeholder({ height = 20, width = "100%" }: { height?: number; width?: string }) {
  return <div className={styles.placeholder} style={{ height, width }} />;
}

const ranges: Array<{ value: AnalyticsRange; label: string }> = [
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "365d", label: "Year" },
];
const statsDateLabel = (date: string, long = false) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(
    "en-US",
    long
      ? { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }
      : { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" },
  );

export function StatsHeader({
  today = "",
  start = "",
  range = "30d",
  onChange,
}: {
  today?: string;
  start?: string;
  range?: AnalyticsRange;
  onChange?: (value: AnalyticsRange) => void;
}) {
  return (
    <div className={stats.header}>
      <div>
        <p className={stats.date}>
          {today ? statsDateLabel(today, true) : <span aria-hidden="true">&nbsp;</span>}
        </p>
        <h2 className={stats.title}>Your progress</h2>
      </div>
      <div className={stats.range}>
        <fieldset disabled={!onChange} className={styles.fieldset}>
          <SegmentedControl
            options={ranges}
            value={range}
            onChange={onChange ?? (() => {})}
            aria-label="Stats range"
          />
        </fieldset>
        <p>
          {start ? (
            `${statsDateLabel(start)} – ${statsDateLabel(today)}`
          ) : (
            <span aria-hidden="true">&nbsp;</span>
          )}
        </p>
      </div>
    </div>
  );
}

export function StatsLoadingBody() {
  return (
    <LoadingRegion label="Loading stats">
      <div className={stats.overview}>
        <div className={stats.metrics}>
          <div className={stats.mainMetric}>
            <Placeholder height={100} width="80%" />
            <p className={stats.label}>Completion rate</p>
            <p>30 days</p>
          </div>
          <div className={stats.metric}>
            <Placeholder height={64} />
            <p className={stats.label}>Perfect days</p>
            <p>in 30 days</p>
          </div>
          <div className={stats.metric}>
            <Placeholder height={64} />
            <p className={stats.label}>Today</p>
            <p>habits complete</p>
          </div>
        </div>
        <div className={stats.consistency}>
          <h3>Consistency</h3>
          <Placeholder height={180} />
        </div>
      </div>
      <div className={stats.habits}>
        <h3>Your habits</h3>
        <div className={stats.listHeader}>
          <span>Habit</span>
          <span>Completion rate</span>
        </div>
        <ul className={stats.list}>
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <div className={stats.habit}>
                <Placeholder height={64} width="64px" />
                <Placeholder width="70%" />
                <Placeholder />
                <Placeholder height={8} />
                <span />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </LoadingRegion>
  );
}

export function HabitDetailLoading() {
  const navigate = useNavigate();
  return (
    <div className={detail.page}>
      <div className={detail.topBar}>
        <button className={detail.backButton} onClick={() => void navigate(-1)} aria-label="Back">
          <ArrowLeftIcon size={20} aria-hidden="true" />
        </button>
        <span className={detail.habitName}>Habit stats</span>
        <span aria-hidden="true" style={{ width: 40, height: 40, flexShrink: 0 }} />
      </div>
      <LoadingRegion label="Loading habit stats">
        <div className={styles.stack}>
          <Placeholder height={44} />
          <div className={detail.section}>
            <h3 className={detail.sectionTitle}>Score trend</h3>
            <Placeholder height={200} />
          </div>
          <Placeholder height={88} />
          <Placeholder height={220} />
        </div>
      </LoadingRegion>
    </div>
  );
}

export function HabitFormLoading({ title = "Edit Habit" }: { title?: string }) {
  const navigate = useNavigate();
  return (
    <div className={form.page}>
      <div className={form.topBar}>
        <button className={form.back} onClick={() => void navigate(-1)}>
          <ArrowLeftIcon size={20} aria-hidden="true" /> Back
        </button>
      </div>
      <h2 className={form.heading}>{title}</h2>
      <LoadingRegion label="Loading habit">
        <div className={styles.stack}>
          <Placeholder height={160} />
          <Placeholder height={200} />
          <Placeholder height={100} />
        </div>
      </LoadingRegion>
    </div>
  );
}

export function AuthLoading() {
  return (
    <div className={styles.auth}>
      <img src="/icons/brand-icon.png" alt="" width={64} height={64} />
      <LoadingRegion label="Signing in">
        <p>Signing in…</p>
      </LoadingRegion>
    </div>
  );
}

export function RouteLoading() {
  const { pathname, search } = useLocation();
  if (pathname === "/stats")
    return (
      <div className={stats.page}>
        <StatsHeader />
        <StatsLoadingBody />
      </div>
    );
  if (/\/habits\/[^/]+\/stats$/.test(pathname)) return <HabitDetailLoading />;
  if (pathname === "/settings")
    return (
      <div className={settings.page}>
        <h2 className={settings.heading}>Settings</h2>
        <div className={settings.sections}>
          <div className={settings.account}>
            <Section title="Account">
              <LoadingRegion label="Loading account">
                <Placeholder height={48} />
              </LoadingRegion>
            </Section>
          </div>
          <div className={settings.sectionSelector} data-settings-section-selector>
            <label htmlFor="loading-settings-section">Section</label>
            <select id="loading-settings-section" disabled value={getSettingsSection(search)}>
              {SETTINGS_SECTIONS.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.label}
                </option>
              ))}
            </select>
          </div>
          <h3 className={settings.panelTitle}>
            {SETTINGS_SECTIONS.find((s) => s.id === getSettingsSection(search))?.label}
          </h3>
          <LoadingRegion label="Loading settings">
            {getSettingsSection(search) === "appearance" ? (
              <div className={appearance.appearance}>
                {[
                  {
                    label: "Theme",
                    names: ["Light", "Dark", "AMOLED", "System"],
                    grid: appearance.themes,
                    sample: appearance.themeSample,
                  },
                  {
                    label: "Card density",
                    names: ["Auto", "Lean", "Dense"],
                    grid: appearance.densities,
                    sample: appearance.densitySample,
                  },
                ].map((group) => (
                  <fieldset key={group.label} className={appearance.fieldset}>
                    <legend>{group.label}</legend>
                    <div className={group.grid}>
                      {group.names.map((name) => (
                        <div key={name}>
                          <div className={group.sample}>
                            <Placeholder height={220} />
                          </div>
                          <div className={appearance.caption}>
                            <span className={styles.radioSpace} />
                            {name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            ) : (
              <div className={styles.stack}>
                {[0, 1, 2].map((i) => (
                  <Placeholder key={i} height={64} />
                ))}
              </div>
            )}
          </LoadingRegion>
        </div>
      </div>
    );
  if (pathname.endsWith("/edit") || pathname === "/habits/new")
    return <HabitFormLoading title={pathname.endsWith("/edit") ? "Edit Habit" : "New Habit"} />;
  // Until local preferences are known, Today could be onboarding or the daily list.
  // Keep the destination canvas quiet instead of flashing the wrong kind of cards.
  return (
    <div className={styles.canvas}>
      <LoadingRegion>
        <div className={styles.preparing}>Getting your habits ready…</div>
      </LoadingRegion>
    </div>
  );
}
